import React, { useState, useEffect, useRef } from "react";
import { Plus, Edit, Trash, Eye, EyeOff, Search } from "lucide-react";
import api from "../../utils/Api";

export default function Empleados() {
  const [empleados, setEmpleados] = useState([]);
  const [total, setTotal] = useState(0);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [selectedEmpleado, setSelectedEmpleado] = useState(null);
  const [formData, setFormData] = useState({
    dni: "", nombre: "", apellidos: "", email_usuario: "",
    telefono_usuario: "", rol: "empleado", estado: "activo",
    password: "", password_confirmation: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [empleadoToDelete, setEmpleadoToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const itemsPerPage = 10;
  const debounceRef = useRef(null);
  const [toast, setToast] = useState({ show: false, type: "", message: "" });

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: "", message: "" }), 4000);
  };

  const fetchEmpleados = async (currentPage = page, currentSearch = search) => {
    try {
      const params = { page: currentPage, per_page: itemsPerPage };
      if (currentSearch) params.search = currentSearch;
      const { data } = await api.get("/usuarios", { params });
      setEmpleados(data.data);
      setTotal(data.total);
    } catch {
      showToast("danger", "Error al cargar empleados");
    }
  };

  useEffect(() => { fetchEmpleados(page, search); }, [page]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); fetchEmpleados(1, value); }, 300);
  };

  const openOffcanvas = (empleado = null) => {
    setSelectedEmpleado(empleado);
    setFormData(empleado
      ? { dni: empleado.dni, nombre: empleado.nombre, apellidos: empleado.apellidos,
          email_usuario: empleado.email, telefono_usuario: empleado.telefono,
          rol: empleado.rol, estado: empleado.estado, password: "", password_confirmation: "" }
      : { dni: "", nombre: "", apellidos: "", email_usuario: "", telefono_usuario: "",
          rol: "empleado", estado: "activo", password: "", password_confirmation: "" }
    );
    setOffcanvasOpen(true);
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedEmpleado) {
        await api.put(`/usuarios/${selectedEmpleado.id}`, formData);
        showToast("success", "Empleado actualizado correctamente");
      } else {
        await api.post("/usuarios", formData);
        showToast("success", "Empleado creado correctamente");
      }
      fetchEmpleados(page, search);
      setOffcanvasOpen(false);
    } catch (error) {
      const errors = error.errors ?? error.response?.data?.errors;
      const firstValidation = errors ? Object.values(errors)[0]?.[0] : null;
      showToast("danger", firstValidation || error.message || error.response?.data?.message || "Error al guardar empleado");
    }
  };

  const confirmDelete = (empleado) => { setEmpleadoToDelete(empleado); setDeleteModalOpen(true); };

  const handleDelete = async () => {
    if (!empleadoToDelete) return;
    try {
      await api.delete(`/usuarios/${empleadoToDelete.id}`);
      showToast("success", "Empleado eliminado correctamente");
      const newPage = empleados.length === 1 && page > 1 ? page - 1 : page;
      setPage(newPage);
      fetchEmpleados(newPage, search);
      setDeleteModalOpen(false);
      setEmpleadoToDelete(null);
    } catch (error) {
      showToast("danger", error.message || "Error al eliminar empleado");
    }
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div>
      {toast.show && (
        <div className={`tn-toast tn-toast--${toast.type}`}>{toast.message}</div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 className="tn-page-title">Empleados</h1>
          <p className="tn-page-sub">{total} empleado{total !== 1 ? "s" : ""} en tu empresa</p>
        </div>
        <button className="tn-btn tn-btn--primary" onClick={() => openOffcanvas()}>
          <Plus size={16} /> Nuevo empleado
        </button>
      </div>

      <div className="tn-card" style={{ marginBottom: 20 }}>
        <div style={{ padding: "14px 16px", position: "relative" }}>
          <Search size={16} style={{ position: "absolute", left: 28, top: "50%", transform: "translateY(-50%)", color: "var(--clr-muted-lt)" }} />
          <input
            type="text"
            className="tn-input"
            style={{ paddingLeft: 38, maxWidth: 340 }}
            placeholder="Buscar por nombre, apellidos o DNI…"
            onChange={handleSearch}
          />
        </div>

        <div className="tn-table-wrap">
          <table className="tn-table">
            <thead>
              <tr>
                <th>DNI</th>
                <th>Nombre</th>
                <th>Apellidos</th>
                <th>Email</th>
                <th>Teléfono</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {empleados.length > 0 ? empleados.map((emp) => (
                <tr key={emp.id}>
                  <td data-label="DNI">{emp.dni}</td>
                  <td data-label="Nombre">{emp.nombre}</td>
                  <td data-label="Apellidos">{emp.apellidos}</td>
                  <td data-label="Email">{emp.email}</td>
                  <td data-label="Teléfono">{emp.telefono}</td>
                  <td data-label="Rol">
                    <span className={emp.rol === "admin" ? "tn-badge tn-badge--orange" : "tn-badge tn-badge--slate"}>
                      {emp.rol}
                    </span>
                  </td>
                  <td data-label="Estado">
                    <span className={emp.estado === "activo" ? "tn-badge tn-badge--green" : "tn-badge tn-badge--red"}>
                      {emp.estado}
                    </span>
                  </td>
                  <td data-label="Acciones">
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="tn-btn tn-btn--ghost" style={{ padding: "6px 10px" }} onClick={() => openOffcanvas(emp)} title="Editar">
                        <Edit size={15} />
                      </button>
                      <button className="tn-btn tn-btn--danger" style={{ padding: "6px 10px" }} onClick={() => confirmDelete(emp)} title="Eliminar">
                        <Trash size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center", padding: "40px 20px", color: "var(--clr-muted)" }}>
                    No hay empleados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="tn-pagination" style={{ padding: "12px 16px", borderTop: "1px solid var(--clr-border)" }}>
            <button className="tn-page-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button key={i} className={`tn-page-btn${page === i + 1 ? " active" : ""}`} onClick={() => setPage(i + 1)}>
                {i + 1}
              </button>
            ))}
            <button className="tn-page-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>›</button>
          </div>
        )}
      </div>

      {/* Modal de confirmación */}
      {deleteModalOpen && (
        <div className="tn-modal-backdrop" onClick={() => setDeleteModalOpen(false)}>
          <div className="tn-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tn-modal__header">
              <span style={{ fontWeight: 700, fontSize: 15 }}>Confirmar eliminación</span>
            </div>
            <div className="tn-modal__body">
              <p style={{ margin: 0, fontSize: 14, color: "var(--clr-text)" }}>
                ¿Estás seguro de que quieres eliminar a <strong>{empleadoToDelete?.nombre} {empleadoToDelete?.apellidos}</strong>? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="tn-modal__footer">
              <button className="tn-btn tn-btn--ghost" onClick={() => setDeleteModalOpen(false)}>Cancelar</button>
              <button className="tn-btn tn-btn--danger" onClick={handleDelete}>Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* Offcanvas */}
      {offcanvasOpen && (
        <>
          <div className="tn-overlay" onClick={() => setOffcanvasOpen(false)} />
          <div className="tn-offcanvas" style={{ display: "flex", flexDirection: "column" }}>
            <div className="tn-offcanvas__header">
              <span>{selectedEmpleado ? "Editar empleado" : "Nuevo empleado"}</span>
              <button onClick={() => setOffcanvasOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--clr-muted)", fontSize: 20, lineHeight: 1 }}>×</button>
            </div>
            <div className="tn-offcanvas__body">
              <form onSubmit={handleSubmit} id="emp-form">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div>
                    <label className="tn-label">DNI</label>
                    <input className="tn-input" type="text" name="dni" value={formData.dni} onChange={handleChange} required />
                  </div>
                  <div>
                    <label className="tn-label">Nombre</label>
                    <input className="tn-input" type="text" name="nombre" value={formData.nombre} onChange={handleChange} required />
                  </div>
                  <div>
                    <label className="tn-label">Apellidos</label>
                    <input className="tn-input" type="text" name="apellidos" value={formData.apellidos} onChange={handleChange} required />
                  </div>
                  <div>
                    <label className="tn-label">Email</label>
                    <input className="tn-input" type="email" name="email_usuario" value={formData.email_usuario} onChange={handleChange} required />
                  </div>
                  <div>
                    <label className="tn-label">Teléfono</label>
                    <input className="tn-input" type="text" name="telefono_usuario" value={formData.telefono_usuario} onChange={handleChange} />
                  </div>
                  <div>
                    <label className="tn-label">Rol</label>
                    <select className="tn-input tn-select" name="rol" value={formData.rol} onChange={handleChange} required>
                      <option value="empleado">Empleado</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="tn-label">Estado</label>
                    <select className="tn-input tn-select" name="estado" value={formData.estado} onChange={handleChange} required>
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginTop: 16, borderTop: "1px solid var(--clr-border)", paddingTop: 16 }}>
                  <p style={{ fontSize: 12, color: "var(--clr-muted)", marginBottom: 10 }}>
                    {selectedEmpleado ? "Deja en blanco para mantener la contraseña actual" : "Contraseña de acceso"}
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    <div>
                      <label className="tn-label">Contraseña</label>
                      <div style={{ position: "relative" }}>
                        <input className="tn-input" type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} style={{ paddingRight: 40 }} />
                        <button type="button" onClick={() => setShowPassword(!showPassword)}
                          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--clr-muted)", display: "flex" }}>
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="tn-label">Confirmar contraseña</label>
                      <div style={{ position: "relative" }}>
                        <input className="tn-input" type={showPasswordConfirm ? "text" : "password"} name="password_confirmation" value={formData.password_confirmation} onChange={handleChange} style={{ paddingRight: 40 }} />
                        <button type="button" onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--clr-muted)", display: "flex" }}>
                          {showPasswordConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="tn-offcanvas__footer">
              <button className="tn-btn tn-btn--ghost" onClick={() => setOffcanvasOpen(false)}>Cancelar</button>
              <button className="tn-btn tn-btn--primary" type="submit" form="emp-form">
                {selectedEmpleado ? "Guardar cambios" : "Crear empleado"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}